import * as React from "react";
import getConfig from "next/config";
import { bookAppointmentRouteMap, PageDataProvider, AppointmentSlotsProvider, TimeFilterContextProvider, AppointmentFormContextProvider, BookAppointmentRoutingContextProvider, bookAppointmentRoutingErrorHandler, AppointmentUtils } from "@grandvisionhq/appointments";
import { useBloomreachPageRoutes, useBloomreachSiteLogos, useBloomreachAppointmentContent, useBloomreachSdpContent, CmsHeadTags, CmsComponentSlot } from "@grandvisionhq/bloomreach";
import { useTryInStoreContext } from "@grandvisionhq/fun-try-in-store";
import { useFeatureToggles, useIntl } from "@grandvisionhq/state";
import { monetate } from "@grandvisionhq/svc-monetate";
import { extractCurrentRoute } from "@grandvisionhq/utils";
import "@grandvisionhq/fun-store-filters/dist/index.css";
const { publicRuntimeConfig } = getConfig();
const selfUrl = publicRuntimeConfig?.selfUrl || "";
const site = publicRuntimeConfig.site || {};
const siteName = publicRuntimeConfig?.site.name || "";
const googleMapsApiKey = publicRuntimeConfig?.googleMaps.apiKey || "";
const Page = (props) => {
  if ("statusCode" in props) {
    throw new Error(
      "This page was designed to show you a meaningful error page but failed showing it."
    );
  }
  const {
    currentRoute,
    queryParams,
    initialProps,
    bookAppointmentForm,
    timeFilterSettings,
    slots
  } = props;
  const { routes } = useBloomreachPageRoutes();
  const siteLogos = useBloomreachSiteLogos();
  const { appointmentDisplayType, appointmentTypes, campaignInfoBanner, appointmentFaq } = useBloomreachAppointmentContent();
  const storesUrl = `${selfUrl}${routes.stores}`;
  const { isTryInStoreEnabled } = useFeatureToggles();
  const { getLabel } = useIntl();
  const { offerFallbackImage: offerFallbackImageUrl } = useBloomreachSdpContent();
  const logo = React.useMemo(() => {
    const url = props.hasAlternativeLogo ? siteLogos.alternative : siteLogos.main;
    return url ? {
      url,
      altText: site?.name ?? "Logo"
    } : void 0;
  }, [siteLogos, props.hasAlternativeLogo]);
  const [tryInstoreStorage, tryInStoreStorageHandlers] = useTryInStoreContext();
  const basePath = routes["appointment-book"] ?? "";
  const initialRoutingState = {
    basePath,
    currentRoute,
    queryParams
  };
  const getInitialAppointmentState = () => {
    const isTryInStoreFlow = typeof queryParams.tryInStoreFlow === "string" && queryParams.tryInStoreFlow === "1";
    if (isTryInStoreEnabled && isTryInStoreFlow) {
      return Object.assign(
        { tryInStoreProductSkus: tryInstoreStorage?.map(({ sku }) => sku) },
        initialProps?.initialAppointmentState
      );
    }
    return initialProps?.initialAppointmentState;
  };
  const initialAppointmentState = getInitialAppointmentState();
  React.useEffect(() => {
    monetate().commitMonetateEvent("appointments");
  }, []);
  if (!(currentRoute in bookAppointmentRouteMap)) {
    console.error(`Invalid book appointment route: ${currentRoute}`);
    return null;
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(CmsHeadTags, { title: getLabel("appointments.page.title") }), /* @__PURE__ */ React.createElement(CmsComponentSlot, { path: "main/top-container" }), /* @__PURE__ */ React.createElement(
    PageDataProvider,
    {
      logo,
      opco: siteName,
      appointmentTypes,
      appointmentDisplayType,
      campaignInfoBanner,
      googleMapsApiKey,
      storesUrl,
      offerFallbackImageUrl,
      appointmentFaq
    },
    /* @__PURE__ */ React.createElement(
      AppointmentSlotsProvider,
      {
        slots: {
          confirmationTime: slots?.confirmationPage?.time,
          createSelectedChoices: slots?.createSelectedChoices,
          clearTryInStoreStorage: tryInStoreStorageHandlers.clear
        }
      },
      /* @__PURE__ */ React.createElement(TimeFilterContextProvider, { configuration: timeFilterSettings }, /* @__PURE__ */ React.createElement(AppointmentFormContextProvider, { Form: bookAppointmentForm }, /* @__PURE__ */ React.createElement(
        BookAppointmentRoutingContextProvider,
        {
          routeMap: bookAppointmentRouteMap,
          initialRoutingState,
          initialAppointmentState
        }
      )))
    )
  ), /* @__PURE__ */ React.createElement(CmsComponentSlot, { path: "main/bottom-container" }));
};
Page.getInitialProps = async (context) => {
  const { asPath = "", req, res, apolloClient, query: queryParams, pathname } = context;
  const locale = req?.locale.toString();
  const currentRoute = extractCurrentRoute(asPath, locale);
  let initialProps = void 0;
  if (!apolloClient) {
    console.error(`ApolloClient not present in context for page ${pathname}`);
    return {
      statusCode: 500
    };
  }
  if (!(currentRoute in bookAppointmentRouteMap)) {
    console.error(`Invalid book appointment route: ${currentRoute}`, {
      asPath,
      currentRoute,
      availableRoutes: Object.keys(bookAppointmentRouteMap)
    });
    const hasQueryParams = Object.keys(queryParams).length > 0;
    if (hasQueryParams) {
      const action = bookAppointmentRoutingErrorHandler(
        "INVALID_ROUTE",
        selfUrl,
        asPath,
        currentRoute,
        queryParams
      );
      if (action.type === "redirect") {
        res?.writeHead(302, {
          Location: action.url
        });
        res?.end();
      }
    }
    return {
      statusCode: 400
    };
  }
  try {
    const {
      storeId,
      typeId,
      start,
      end,
      appointmentId,
      linkedAppointmentTypeId,
      alternativeLinkedAppointmentTypeId
    } = queryParams;
    const { virtualStoreCode, virtualAppointmentTypeId, alternativeUrlStores } = AppointmentUtils.getRuntimeConfigValues(publicRuntimeConfig);
    initialProps = await BookAppointmentRoutingContextProvider?.getServerSideProps(
      apolloClient,
      currentRoute,
      bookAppointmentRouteMap,
      {
        storeId: AppointmentUtils.fromArrayToString(storeId),
        typeId: AppointmentUtils.fromArrayToString(typeId),
        linkedAppointmentTypeId: AppointmentUtils.fromArrayToString(linkedAppointmentTypeId),
        alternativeLinkedAppointmentTypeId: AppointmentUtils.fromArrayToString(
          alternativeLinkedAppointmentTypeId
        ),
        start: AppointmentUtils.fromArrayToString(start),
        end: AppointmentUtils.fromArrayToString(end),
        appointmentId: AppointmentUtils.fromArrayToString(appointmentId),
        virtualAppointmentTypeId,
        virtualStoreCode,
        alternativeUrlStores,
        tryInStoreFlow: AppointmentUtils.fromArrayToString(queryParams.tryInStoreFlow),
        groupId: queryParams.groupId !== null ? AppointmentUtils.fromArrayToString(queryParams.groupId) : void 0
      },
      locale
    );
  } catch (error) {
    console.error("Routing error", error, {
      queryParams,
      currentRoute,
      asPath,
      availableRoutes: Object.keys(bookAppointmentRouteMap),
      errorMessage: error?.message
    });
    const errorMessage = error?.message ?? "UNKNOWN_ERROR";
    const hasQueryParams = Object.keys(queryParams).length > 0;
    if (hasQueryParams) {
      const action = bookAppointmentRoutingErrorHandler(
        errorMessage,
        selfUrl,
        asPath,
        currentRoute,
        queryParams
      );
      if (action.type === "redirect") {
        res?.writeHead(302, {
          Location: action.url
        });
        res?.end();
      }
    }
    return {
      statusCode: 400
    };
  }
  return {
    currentRoute,
    queryParams,
    initialProps
  };
};
export {
  Page
};
//# sourceMappingURL=book-appointment-v3.js.map
